import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { pageMetadata, companyInfo } from '../data';
import EventCard from '../components/EventCard';

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('kommende'); // 'kommende' or 'afholdt'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch all events from CMS
  useEffect(() => {
    // Set page title and meta description
    document.title = pageMetadata.events.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', pageMetadata.events.description);

    const fetchEvents = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/events?limit=50&sort=date&order=asc');
        if (res.ok) {
          const data = await res.json();
          const allEvents = data.items || [];
          setEvents(allEvents);

          // Check if there is an event ID in the URL params to open
          const eventId = searchParams.get('event');
          if (eventId) {
            const foundEvent = allEvents.find(e => e.id === eventId);
            if (foundEvent) setSelectedEvent(foundEvent);
          }
        }
      } catch (e) {
        console.error('Error fetching events:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [searchParams]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setSearchParams({ event: event.id });
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setSearchParams({});
  };

  // Filter events based on active tab
  const filteredEvents = events.filter(e => e.status === activeTab);

  // Format date nicely
  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
      {/* Page Header */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Event kalender</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl text-brand-dark tracking-wide font-display">
          Begivenheder & Cirkler
        </h1>
        <p className="text-brand-muted font-sans text-base sm:text-lg">
          Her kan du finde en oversigt over alle mine ceremonier, forløb og retreats. Vælg mellem kommende begivenheder eller se tidligere afholdte events.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center border-b border-brand-sand max-w-md mx-auto">
        <button
          onClick={() => setActiveTab('kommende')}
          className={`flex-1 py-4 text-center font-sans text-sm font-semibold tracking-wider uppercase transition-all duration-300 border-b-2 ${
            activeTab === 'kommende'
              ? 'border-brand-green text-brand-green'
              : 'border-transparent text-brand-muted hover:text-brand-dark'
          }`}
        >
          Kommende events
        </button>
        <button
          onClick={() => setActiveTab('afholdt')}
          className={`flex-1 py-4 text-center font-sans text-sm font-semibold tracking-wider uppercase transition-all duration-300 border-b-2 ${
            activeTab === 'afholdt'
              ? 'border-brand-green text-brand-green'
              : 'border-transparent text-brand-muted hover:text-brand-dark'
          }`}
        >
          Afholdte events
        </button>
      </div>

      {/* Events Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <img 
            src="https://assets.auxodev.dk/assets/9c094138-0bed-486b-b914-79750263377d/tribe-loading.gif" 
            alt="Indlæser..." 
            className="h-10 w-auto"
          />
        </div>
      ) : filteredEvents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredEvents.map((event) => (
            <EventCard 
              key={event.id} 
              event={event} 
              onSelect={handleSelectEvent}
            />
          ))}
        </div>
      ) : (
        <div className="bg-brand-sand/30 rounded-2xl p-12 text-center border border-brand-sand max-w-2xl mx-auto space-y-6">
          <p className="text-brand-muted font-sans text-lg">
            {activeTab === 'kommende' 
              ? 'Der er i øjeblikket ingen planlagte kommende events. Du er velkommen til at kontakte mig for at arrangere et event.'
              : 'Der er ingen registrerede tidligere events.'}
          </p>
          {activeTab === 'kommende' && (
            <Link
              to="/kontakt"
              className="inline-block bg-brand-green hover:bg-brand-green/90 text-brand-cream px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-all duration-300 shadow-sm"
            >
              Kontakt mig
            </Link>
          )}
        </div>
      )}

      {/* Event Details Modal */}
      {selectedEvent && (
        <div 
          className="fixed inset-0 z-50 bg-brand-dark/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={handleCloseModal}
        >
          <div 
            className="bg-brand-cream rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-brand-sand relative flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Image */}
            <div className="relative aspect-[21/9] w-full bg-brand-sand">
              <img 
                src={selectedEvent.image && selectedEvent.image[0] ? selectedEvent.image[0] : 'https://assets.auxodev.dk/assets/9c094138-0bed-486b-b914-79750263377d/25-johanne-eis-1920x900-7n-1.webp'} 
                alt={selectedEvent.title} 
                className="w-full h-full object-cover"
              />
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 bg-brand-cream/80 hover:bg-brand-cream text-brand-dark p-2.5 rounded-full transition-colors duration-300 shadow-md focus:outline-none"
                aria-label="Luk vindue"
              >
                <i className="fa-light fa-xmark text-xl" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-10 space-y-8 text-left">
              <div className="space-y-4">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase ${
                  selectedEvent.status === 'kommende' 
                    ? 'bg-brand-green text-brand-cream' 
                    : 'bg-brand-muted/20 text-brand-muted'
                }`}>
                  {selectedEvent.status === 'kommende' ? 'Kommende' : 'Afholdt'}
                </span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-brand-dark leading-tight tracking-wide">
                  {selectedEvent.title}
                </h2>
              </div>

              {/* Quick Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6 bg-brand-sand/30 rounded-2xl border border-brand-sand text-sm font-sans text-brand-muted">
                <div className="flex items-start gap-3">
                  <i className="fa-light fa-calendar text-brand-green text-lg mt-0.5" />
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-brand-muted/70">Dato</span>
                    <span className="font-medium text-brand-dark">{formatDate(selectedEvent.date)}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="fa-light fa-clock text-brand-green text-lg mt-0.5" />
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-brand-muted/70">Tidspunkt</span>
                    <span className="font-medium text-brand-dark">{selectedEvent.time}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="fa-light fa-location-dot text-brand-green text-lg mt-0.5" />
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-brand-muted/70">Lokation</span>
                    <span className="font-medium text-brand-dark">{selectedEvent.location}</span>
                  </div>
                </div>
              </div>

              {/* Long Description (HTML) */}
              <div 
                className="prose max-w-none font-sans text-brand-muted leading-relaxed space-y-4 
                  prose-headings:font-display prose-headings:text-brand-dark prose-headings:tracking-wide
                  prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                  prose-p:mb-4 prose-ul:list-disc prose-ul:pl-5 prose-ul:space-y-2"
                dangerouslySetInnerHTML={{ __html: selectedEvent.long_description }}
              />

              {/* Footer / Booking */}
              <div className="pt-8 border-t border-brand-sand flex flex-col sm:flex-row items-center justify-between gap-6">
                <div>
                  <span className="block text-xs text-brand-muted uppercase tracking-wider font-sans">Pris pr. deltager</span>
                  <span className="font-display text-2xl font-semibold text-brand-dark">{selectedEvent.price}</span>
                </div>
                <div className="flex flex-wrap gap-4 w-full sm:w-auto">
                  {selectedEvent.status === 'kommende' ? (
                    selectedEvent.signup_link.startsWith('http') ? (
                      <a
                        href={selectedEvent.signup_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full sm:w-auto text-center bg-brand-green hover:bg-brand-green/90 text-brand-cream px-8 py-3.5 rounded-full text-base font-medium tracking-wide transition-all duration-300 shadow-md"
                      >
                        Tilmeld dig her <i className="fa-light fa-arrow-up-right-from-square ml-2" />
                      </a>
                    ) : (
                      <Link
                        to="/kontakt"
                        onClick={handleCloseModal}
                        className="w-full sm:w-auto text-center bg-brand-green hover:bg-brand-green/90 text-brand-cream px-8 py-3.5 rounded-full text-base font-medium tracking-wide transition-all duration-300 shadow-md"
                      >
                        Tilmeld dig her
                      </Link>
                    )
                  ) : (
                    <Link
                      to="/kontakt"
                      onClick={handleCloseModal}
                      className="w-full sm:w-auto text-center bg-brand-sand hover:bg-brand-beige text-brand-dark/80 px-8 py-3.5 rounded-full text-base font-medium tracking-wide transition-all duration-300"
                    >
                      Kontakt for forespørgsel
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
